export const CLUB_CAPABILITY = Object.freeze({
  RESERVATION: "reservation",
  PLAYER_BOOKING: "player_booking",
  PAID_SLOTS: "paid_slots",
  SEASON: "season",
  MEMBERSHIP: "membership",
  COACHING: "coaching",
  HOURLY: "hourly",
  CARD_PAYMENTS: "card_payments",
});

export const PAY_ANSWER = Object.freeze({
  TIME: "time",
  SEASON: "season",
  MEMBERSHIP: "membership",
  LATER: "later",
});

export const BOOKER_ANSWER = Object.freeze({
  PLAYERS: "players",
  STAFF: "staff",
  BOTH: "both",
  NONE: "none",
});

export const COLLECT_ANSWER = Object.freeze({
  CARDS: "cards",
  OFFLINE: "offline",
});

export const CALENDAR_TYPE = Object.freeze({
  RESERVATION: "RESERVATION_CALENDAR",
  EVENT: "EVENT_CALENDAR",
});

export const CHANGE_CLUB_MODEL = Object.freeze({
  title: "Change how this club works?",
  body: "Your existing calendar can be affected. Reservation, who can book, carts, and Events may no longer match what you already set up. Bookings and forms already created stay until you update them.",
  confirm: "Continue",
  cancel: "Cancel",
});

export const HOW_YOU_CHARGE = Object.freeze({
  kicker: "Built around your club",
  title: "How You Charge",
  lead: "A few questions. We name the calendar that fits — court bookings, programs, or both kinds of work.",
  empty: "Answer this to continue.",
  nextLabel: "What you get",
  calendarLabel: "Your calendar",
  previewKicker: "Your calendar",
  previewPending: "Keep answering. We will name the calendar that fits your club.",
  afterThis: "Next you pick your SportMingle plan. If you take cards, Financial Vendor is the step after that — not an error on Plan.",
  reviewHelp: "This is what we will set up. Change an answer if it looks wrong.",
  continueLabel: "Continue",
  questionOf: (current, total) => `Question ${current} of ${total}`,
});

export function isPayQuestionReady(answers) {
  return normalizeClubCapabilityAnswers(answers).pay.length > 0;
}

export function isBookersQuestionReady(answers) {
  return Boolean(normalizeClubCapabilityAnswers(answers).bookers);
}

export function isCoachingQuestionReady(answers) {
  return normalizeClubCapabilityAnswers(answers).coaching !== null;
}

export function isCollectQuestionReady(answers) {
  return normalizeClubCapabilityAnswers(answers).collect.length > 0;
}

export const SETUP_QUESTIONS = Object.freeze({
  pay: {
    id: "pay",
    prompt: "How do customers pay you?",
    help: "Check every way that applies. You can select more than one. This does not pick your calendar.",
    example: "A season or class fee is one way to get paid. Hourly court time is another. Whether you operate the rink is the next question — two clubs can charge a season and still need different calendars.",
    options: [
      { id: PAY_ANSWER.TIME, label: "For time they book", hint: "Players pay per hour or half hour on a court or rink." },
      { id: PAY_ANSWER.SEASON, label: "For a season or smaller program", hint: "Families pay for a term. This is true whether you own the ice or only run the program." },
      { id: PAY_ANSWER.MEMBERSHIP, label: "For a membership or subscription", hint: "A monthly or yearly club plan." },
      { id: PAY_ANSWER.LATER, label: "Not in SportMingle yet", hint: "You can add payments after setup." },
    ],
  },
  bookers: {
    id: "bookers",
    prompt: "Do you operate a court, rink, or field?",
    help: "Say yes only if this club controls the space. If you run programs at a rink someone else owns, you do not need a booking grid.",
    example: "You own or run the ice → court / rink calendar. You only host a season or class there → programs calendar.",
    options: [
      { id: BOOKER_ANSWER.STAFF, label: "Yes — staff and coaches book it", hint: "You operate the space. Staff put ice or court time on the grid. Players do not pick a slot." },
      { id: BOOKER_ANSWER.PLAYERS, label: "Yes — players book themselves", hint: "You operate the space. Customers pick a time, like hourly badminton." },
      { id: BOOKER_ANSWER.BOTH, label: "Yes — staff and players both book", hint: "You operate the space. Coaches block clinics; members book the rest." },
      { id: BOOKER_ANSWER.NONE, label: "No — we only run programs there", hint: "You do not own the rink or courts. Seasons and classes use the programs calendar." },
    ],
  },
  coaching: {
    id: "coaching",
    prompt: "Do customers also buy a class or a lesson?",
    help: "Not ice or court time by itself. A class is something they join, like learn-to-skate or a tennis lesson.",
    example: "A season plus learn-to-skate or a special class → Yes. Tennis lessons → Yes. Only a season or hourly court, no classes → No.",
    options: [
      { id: true, label: "Yes, they buy classes or lessons", hint: "Including learn-to-skate. That lives under Programs → Events, with your forms. No cart." },
      { id: false, label: "No, they only pay for a season or court time", hint: "No learn-to-skate or lesson sign-up." },
    ],
  },
  collect: {
    id: "collect",
    prompt: "How do you want to get paid?",
    help: "Check every way you collect money. Most clubs take cards and still accept cash or check.",
    example: "Players pay in the cart → Cards. You also take cash or check and mark it paid → check both.",
    options: [
      {
        id: COLLECT_ANSWER.CARDS,
        label: "Cards (cart / online)",
        hint: "Players pay in the app. After your plan, finish Financial Vendor. Choosing cards does not create Stripe now.",
      },
      {
        id: COLLECT_ANSWER.OFFLINE,
        label: "Cash or check",
        hint: "You mark it paid. No Financial Vendor and no SportMingle card fee for those payments.",
      },
    ],
  },
});

const STORAGE_PREFIX = "sportmingle.clubCapabilities.";

const KNOWN_IDS = new Set([
  CLUB_CAPABILITY.RESERVATION,
  CLUB_CAPABILITY.PLAYER_BOOKING,
  CLUB_CAPABILITY.PAID_SLOTS,
  CLUB_CAPABILITY.SEASON,
  CLUB_CAPABILITY.MEMBERSHIP,
  CLUB_CAPABILITY.COACHING,
  CLUB_CAPABILITY.CARD_PAYMENTS,
]);

const emptyAnswers = () => ({
  pay: [],
  bookers: "",
  coaching: null,
  collect: [],
});

export function normalizeCollectAnswers(value) {
  if (Array.isArray(value)) {
    return value.filter((id) => Object.values(COLLECT_ANSWER).includes(id));
  }
  if (Object.values(COLLECT_ANSWER).includes(value)) {
    return [value];
  }
  return [];
}

export function toggleCollectAnswer(collect, id) {
  const current = normalizeCollectAnswers(collect);
  if (!Object.values(COLLECT_ANSWER).includes(id)) {
    return current;
  }
  if (current.includes(id)) {
    return current.filter((item) => item !== id);
  }
  return [...current, id];
}

export function defaultClubCapabilityAnswers() {
  return emptyAnswers();
}

export function normalizeClubCapabilities(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  const next = [];
  value.forEach((id) => {
    const key = String(id);
    if (key === CLUB_CAPABILITY.HOURLY) {
      next.push(
        CLUB_CAPABILITY.RESERVATION,
        CLUB_CAPABILITY.PLAYER_BOOKING,
        CLUB_CAPABILITY.PAID_SLOTS
      );
      return;
    }
    if (KNOWN_IDS.has(key)) {
      next.push(key);
    }
  });
  const unique = [...new Set(next)];
  if (unique.includes(CLUB_CAPABILITY.PAID_SLOTS)) {
    if (!unique.includes(CLUB_CAPABILITY.PLAYER_BOOKING)) {
      unique.push(CLUB_CAPABILITY.PLAYER_BOOKING);
    }
    if (!unique.includes(CLUB_CAPABILITY.RESERVATION)) {
      unique.push(CLUB_CAPABILITY.RESERVATION);
    }
  }
  if (unique.includes(CLUB_CAPABILITY.PLAYER_BOOKING) && !unique.includes(CLUB_CAPABILITY.RESERVATION)) {
    unique.push(CLUB_CAPABILITY.RESERVATION);
  }
  return unique;
}

export function normalizeClubCapabilityAnswers(value = {}) {
  const pay = Array.isArray(value.pay) ? value.pay.map(String) : [];
  let nextPay = pay.filter((id) => Object.values(PAY_ANSWER).includes(id));
  if (nextPay.includes(PAY_ANSWER.LATER) && nextPay.length > 1) {
    nextPay = nextPay.filter((id) => id !== PAY_ANSWER.LATER);
  }
  const bookers = Object.values(BOOKER_ANSWER).includes(value.bookers) ? value.bookers : "";
  const coaching = value.coaching === true || value.coaching === false ? value.coaching : null;
  const collect = normalizeCollectAnswers(value.collect);
  return { pay: nextPay, bookers, coaching, collect };
}

export function togglePayAnswer(pay, id) {
  const current = Array.isArray(pay) ? pay : [];
  if (id === PAY_ANSWER.LATER) {
    return current.includes(PAY_ANSWER.LATER) ? [] : [PAY_ANSWER.LATER];
  }
  const withoutLater = current.filter((item) => item !== PAY_ANSWER.LATER);
  if (withoutLater.includes(id)) {
    return withoutLater.filter((item) => item !== id);
  }
  return [...withoutLater, id];
}

export function capabilitiesFromAnswers(answers) {
  const { pay, bookers, coaching, collect } = normalizeClubCapabilityAnswers(answers);
  const next = [];
  const paysForTime = pay.includes(PAY_ANSWER.TIME);
  const wantsReservation = paysForTime
    || bookers === BOOKER_ANSWER.STAFF
    || bookers === BOOKER_ANSWER.PLAYERS
    || bookers === BOOKER_ANSWER.BOTH;

  if (wantsReservation) {
    next.push(CLUB_CAPABILITY.RESERVATION);
  }
  if (paysForTime || bookers === BOOKER_ANSWER.PLAYERS || bookers === BOOKER_ANSWER.BOTH) {
    next.push(CLUB_CAPABILITY.PLAYER_BOOKING);
  }
  if (paysForTime) {
    next.push(CLUB_CAPABILITY.PAID_SLOTS);
  }
  if (pay.includes(PAY_ANSWER.SEASON)) {
    next.push(CLUB_CAPABILITY.SEASON);
  }
  if (pay.includes(PAY_ANSWER.MEMBERSHIP)) {
    next.push(CLUB_CAPABILITY.MEMBERSHIP);
  }
  if (coaching === true) {
    next.push(CLUB_CAPABILITY.COACHING);
  }
  if (collect.includes(COLLECT_ANSWER.CARDS)) {
    next.push(CLUB_CAPABILITY.CARD_PAYMENTS);
  }
  return normalizeClubCapabilities(next);
}

export function answersAreReady(answers) {
  const { pay, bookers, coaching, collect } = normalizeClubCapabilityAnswers(answers);
  return pay.length > 0 && Boolean(bookers) && coaching !== null && collect.length > 0;
}

export function clubCapabilitiesStorageKey(clubId) {
  return `${STORAGE_PREFIX}${clubId}`;
}

export function isSavedCalendarType(calendarType) {
  const type = String(calendarType || "").trim().toUpperCase();
  return type === CALENDAR_TYPE.EVENT || type === CALENDAR_TYPE.RESERVATION;
}

export function clubCalendarType(club) {
  return String(club?.calendarType || "").trim().toUpperCase();
}

export function clearClubCapabilitiesCache(clubId) {
  if (!clubId || typeof localStorage === "undefined") {
    return;
  }
  localStorage.removeItem(clubCapabilitiesStorageKey(clubId));
}

export function readClubCapabilityRecord() {
  return { capabilities: [], answers: emptyAnswers() };
}

export function readClubCapabilities() {
  return [];
}

export function hasSavedClubCapabilities(clubOrId) {
  if (clubOrId && typeof clubOrId === "object") {
    return isSavedCalendarType(clubOrId.calendarType);
  }
  return false;
}

export function saveClubCapabilities(clubId, capabilities, answers) {
  clearClubCapabilitiesCache(clubId);
  return {
    capabilities: normalizeClubCapabilities(capabilities),
    answers: answers ? normalizeClubCapabilityAnswers(answers) : emptyAnswers(),
  };
}

export function calendarTypeFromCapabilities(capabilities) {
  return normalizeClubCapabilities(capabilities).includes(CLUB_CAPABILITY.RESERVATION)
    ? CALENDAR_TYPE.RESERVATION
    : CALENDAR_TYPE.EVENT;
}

export function isPlanRequiredFromCapabilities(capabilities) {
  return normalizeClubCapabilities(capabilities).includes(CLUB_CAPABILITY.MEMBERSHIP);
}

export function generatedSetupLines(capabilities, answers) {
  const ids = normalizeClubCapabilities(capabilities);
  if (!ids.length) {
    return [];
  }
  const collect = normalizeCollectAnswers(answers?.collect);
  const takesCards = ids.includes(CLUB_CAPABILITY.CARD_PAYMENTS) || collect.includes(COLLECT_ANSWER.CARDS);
  const takesOffline = collect.includes(COLLECT_ANSWER.OFFLINE) || (!takesCards && collect.length === 0);
  const lines = [];
  if (ids.includes(CLUB_CAPABILITY.RESERVATION)) {
    const who = ids.includes(CLUB_CAPABILITY.PLAYER_BOOKING)
      ? "Players can book, and staff can too."
      : "Staff and coaches book. Players do not book themselves.";
    lines.push(`Court / rink calendar. ${who}`);
  } else {
    lines.push("Programs calendar. No court booking grid.");
  }
  if (ids.includes(CLUB_CAPABILITY.PAID_SLOTS) && takesCards) {
    lines.push("A cart only when someone pays for an hour or 30 minutes on the court or rink.");
  } else if (ids.includes(CLUB_CAPABILITY.PAID_SLOTS)) {
    lines.push("Hourly or 30-minute court time is marked paid offline. No card cart.");
  } else {
    lines.push("No cart. Carts are only for hourly or 30-minute court time.");
  }
  if (takesCards && takesOffline && collect.includes(COLLECT_ANSWER.OFFLINE)) {
    lines.push("Cards and cash or check. Finish Financial Vendor after your plan so players can pay by card.");
  } else if (takesCards) {
    lines.push("Card payments. Finish Financial Vendor after your plan so players can check out.");
  } else {
    lines.push("Cash or check only. No Financial Vendor and no SportMingle card fee.");
  }
  if (ids.includes(CLUB_CAPABILITY.SEASON)) {
    lines.push("Programs for seasons and smaller programs.");
  }
  if (ids.includes(CLUB_CAPABILITY.MEMBERSHIP)) {
    lines.push("Customer membership plans.");
  }
  if (ids.includes(CLUB_CAPABILITY.COACHING)) {
    lines.push("Classes and lessons on Events (sidebar → Programs → Events) with your custom forms. No cart.");
  }
  return lines;
}

export function generatedCalendarGuide(capabilities) {
  const ids = normalizeClubCapabilities(capabilities);
  if (ids.includes(CLUB_CAPABILITY.RESERVATION) && ids.includes(CLUB_CAPABILITY.PAID_SLOTS)) {
    return [
      "On Calendar setup, add each court, rink, or field. One space is enough.",
      "Set open hours and the rate customers pay for booked time.",
      "Players book on Reservation. A cart is used only for that hourly or 30-minute payment.",
    ];
  }
  if (ids.includes(CLUB_CAPABILITY.RESERVATION) && !ids.includes(CLUB_CAPABILITY.PLAYER_BOOKING)) {
    return [
      "On Calendar setup, add the space staff will schedule. One rink or court is enough.",
      "Set open hours. Do not set a customer hourly rate or cart.",
      "Only staff and coaches place bookings on Reservation. Players do not book themselves.",
    ];
  }
  if (ids.includes(CLUB_CAPABILITY.RESERVATION)) {
    return [
      "On Calendar setup, add spaces and open hours.",
      "Players can book. There is no hourly cart unless you add paid time later.",
    ];
  }
  if (ids.includes(CLUB_CAPABILITY.COACHING)) {
    return [
      "Calendar setup skips the reservation grid.",
      "Put classes on Events in the sidebar. Charge with your custom form if the class is paid. That is not a cart.",
    ];
  }
  const lines = ["Calendar setup skips the reservation grid. You do not name courts or hourly rates."];
  if (ids.includes(CLUB_CAPABILITY.SEASON)) {
    lines.push("Use Programs for the season and smaller programs.");
  }
  return lines;
}

export function generatedSetupExample(answers) {
  const { pay, bookers, coaching } = normalizeClubCapabilityAnswers(answers);
  if (pay.includes(PAY_ANSWER.SEASON) && bookers === BOOKER_ANSWER.NONE) {
    return "You run a season or class at a facility you do not own. Programs calendar. No rink grid.";
  }
  if (pay.includes(PAY_ANSWER.SEASON) && bookers === BOOKER_ANSWER.STAFF && coaching !== true) {
    return "You operate the ice or courts. Staff put the season on the grid. Players do not book themselves.";
  }
  if (pay.includes(PAY_ANSWER.TIME) && bookers === BOOKER_ANSWER.PLAYERS) {
    return "You operate the courts. Players book time and can also hold a membership.";
  }
  if (bookers === BOOKER_ANSWER.BOTH || (pay.includes(PAY_ANSWER.TIME) && coaching === true)) {
    return "You operate the courts. Staff block clinics, members book the rest, and you can add lessons.";
  }
  return "";
}

export function recommendedUseCase(answers) {
  const example = generatedSetupExample(answers);
  if (example) {
    return example;
  }
  const { pay, bookers } = normalizeClubCapabilityAnswers(answers);
  if (pay.includes(PAY_ANSWER.TIME)) {
    return "Best when this club operates the courts or rink and rents time by the hour.";
  }
  if (pay.includes(PAY_ANSWER.SEASON) && bookers === BOOKER_ANSWER.STAFF) {
    return "Best when this club operates the ice or courts and staff place the bookings.";
  }
  if (bookers === BOOKER_ANSWER.NONE) {
    return "Best when you run programs at a facility you do not own.";
  }
  return "";
}

const reservationPreview = (fit, extra = {}) => ({
  pending: false,
  type: CALENDAR_TYPE.RESERVATION,
  name: "Court / rink calendar",
  why: "A grid of courts, rinks, or fields. Staff and (if you said so) players place bookings.",
  sidebar: "You will open Calendar → Schedule.",
  fit,
  ...extra,
});

const eventPreview = (fit, extra = {}) => ({
  pending: false,
  type: CALENDAR_TYPE.EVENT,
  name: "Programs calendar",
  why: "Seasons, classes, and lessons. No court booking grid.",
  sidebar: "You will open Calendar → Coach Schedule.",
  fit,
  ...extra,
});

export function calendarPreviewFromAnswers(answers) {
  const normalized = normalizeClubCapabilityAnswers(answers);
  const caps = capabilitiesFromAnswers(normalized);
  const fit = recommendedUseCase(normalized);
  const hasReservation = caps.includes(CLUB_CAPABILITY.RESERVATION);

  if (!normalized.pay.length && !normalized.bookers) {
    return {
      pending: true,
      type: "",
      name: "",
      why: HOW_YOU_CHARGE.previewPending,
      sidebar: "",
      fit: "",
    };
  }

  if (!normalized.bookers) {
    if (normalized.pay.includes(PAY_ANSWER.TIME)) {
      return reservationPreview(fit || "Best when this club operates the courts or rink and rents time by the hour.");
    }
    if (normalized.pay.includes(PAY_ANSWER.SEASON) && !normalized.pay.includes(PAY_ANSWER.TIME)) {
      return {
        pending: true,
        tentative: true,
        type: "",
        name: "",
        why: "A season can go either way. If you operate the rink or courts, you get a booking grid. If you only run programs at a facility you do not own, you get a programs calendar.",
        sidebar: "The next question decides the calendar.",
        fit: "Two clubs can charge a season and still need different calendars.",
      };
    }
    return {
      pending: true,
      type: "",
      name: "",
      why: HOW_YOU_CHARGE.previewPending,
      sidebar: "",
      fit,
    };
  }

  if (hasReservation) {
    return reservationPreview(fit);
  }
  return eventPreview(fit);
}

export function isStarterCourtName(name) {
  const normalized = String(name ?? "").trim().replace(/[_-]/g, " ");
  return /^court\s*1$/i.test(normalized);
}

export function clubCapabilitiesNextHint(capabilities) {
  const ids = normalizeClubCapabilities(capabilities);
  if (!ids.length) {
    return HOW_YOU_CHARGE.empty;
  }
  return [HOW_YOU_CHARGE.afterThis, ...generatedSetupLines(ids)].join(" ");
}

export function applyClubCapabilities(club) {
  if (club?.clubId) {
    clearClubCapabilitiesCache(club.clubId);
  }
  return club;
}

export function capabilitiesForClub(club) {
  if (Array.isArray(club?.clubCapabilities) && club.clubCapabilities.length) {
    return normalizeClubCapabilities(club.clubCapabilities);
  }
  return [];
}

export function hasClubCapabilityModel(club) {
  return capabilitiesForClub(club).length > 0;
}

export function clubHasReservation(club) {
  return clubCalendarType(club) === CALENDAR_TYPE.RESERVATION;
}

export function clubShowsCourtNames(club) {
  return clubHasReservation(club);
}

export function clubCanAddCourt(club, canCreate) {
  return Boolean(canCreate) && clubShowsCourtNames(club);
}

export function clubShowsOpenHours() {
  return true;
}

export function clubShowsReservationInterval(club) {
  return clubShowsCourtNames(club);
}

export function clubShowsPlayerBookingDefault(club) {
  if (!hasClubCapabilityModel(club)) {
    return true;
  }
  return capabilitiesForClub(club).includes(CLUB_CAPABILITY.PLAYER_BOOKING);
}

export function clubCollectAnswers(club) {
  if (club?.clubAnswers?.collect !== undefined) {
    return normalizeCollectAnswers(club.clubAnswers.collect);
  }
  return [];
}

export function clubCollectAnswer(club) {
  const collect = clubCollectAnswers(club);
  if (collect.includes(COLLECT_ANSWER.CARDS) && collect.includes(COLLECT_ANSWER.OFFLINE)) {
    return COLLECT_ANSWER.CARDS;
  }
  return collect[0] || "";
}

export function clubWantsCardPayments(club) {
  const caps = capabilitiesForClub(club);
  const collect = clubCollectAnswers(club);
  if (collect.includes(COLLECT_ANSWER.CARDS)) {
    return true;
  }
  if (collect.includes(COLLECT_ANSWER.OFFLINE)) {
    return false;
  }
  if (caps.includes(CLUB_CAPABILITY.CARD_PAYMENTS)) {
    return true;
  }
  return caps.includes(CLUB_CAPABILITY.PAID_SLOTS);
}

export function clubShowsScheduleCheckout(club) {
  if (!hasClubCapabilityModel(club)) {
    return false;
  }
  return capabilitiesForClub(club).includes(CLUB_CAPABILITY.PAID_SLOTS)
    && clubWantsCardPayments(club);
}

export function calendarSetupLead(club) {
  if (!hasClubCapabilityModel(club)) {
    const type = clubCalendarType(club);
    if (type === CALENDAR_TYPE.EVENT) {
      return "This club is on Coach Schedule. Change answers to switch how Calendar works.";
    }
    if (type === CALENDAR_TYPE.RESERVATION) {
      return "This club is on the reservation calendar. Change answers to switch how Calendar works.";
    }
    return "Save How You Charge so this club has a calendar type.";
  }
  if (!clubHasReservation(club)) {
    return "Calendar stays on Coach Schedule. Court names and reservation slots stay hidden. Set open and close hours for the board.";
  }
  if (clubShowsScheduleCheckout(club)) {
    return "Calendar stays on. Add each court or rink, set open hours, and the rate for booked time.";
  }
  if (!clubShowsPlayerBookingDefault(club)) {
    return "Calendar stays on. Add the space staff will book. Players do not book themselves.";
  }
  return "Calendar stays on. Add spaces and open hours.";
}

export function clubShowsCustomerPlans(club) {
  if (!hasClubCapabilityModel(club)) {
    return true;
  }
  return capabilitiesForClub(club).includes(CLUB_CAPABILITY.MEMBERSHIP);
}

export function clubShowsHourlyCourtPlans(club) {
  if (!hasClubCapabilityModel(club)) {
    return true;
  }
  return capabilitiesForClub(club).includes(CLUB_CAPABILITY.PAID_SLOTS);
}

export function clubShowsClubPlayerCatalog(club) {
  return clubShowsCustomerPlans(club) || clubShowsHourlyCourtPlans(club);
}

export function planRequiredPayload(clubId, capabilities) {
  return {
    clubId,
    isPlanRequired: isPlanRequiredFromCapabilities(capabilities),
  };
}
